import { signOut } from "../lib/auth.js";
import { hashForRoute, type Route } from "../lib/hashRoute.js";
import { shellTitleForRoute, useDocumentTitle } from "../lib/pageTitle.js";
import { Dashboard } from "../pages/Dashboard.js";
import { Projects } from "../pages/Projects.js";
import { ProjectDetail } from "../pages/ProjectDetail.js";
import { BookAssembly } from "../pages/BookAssembly.js";
import { ItemDetail } from "../pages/ItemDetail.js";
import { NewBook } from "../pages/NewBook.js";
import { ReviewQueue } from "../pages/ReviewQueue.js";
import { Jobs } from "../pages/Jobs.js";
import { RoutesPage } from "../pages/Routes.js";
import { Templates } from "../pages/Templates.js";
import { Tokens } from "../pages/Tokens.js";
import { Exports } from "../pages/Exports.js";

const NAV: Array<{ route: Route; label: string }> = [
  { route: { kind: "dashboard" }, label: "Dashboard" },
  { route: { kind: "projects" }, label: "Projects" },
  { route: { kind: "newBook" }, label: "New book" },
  { route: { kind: "review" }, label: "Review" },
  { route: { kind: "jobs" }, label: "Jobs" },
  { route: { kind: "routes" }, label: "Routes" },
  { route: { kind: "templates" }, label: "Templates" },
  { route: { kind: "tokens" }, label: "Tokens" },
  { route: { kind: "exports" }, label: "Exports" },
];

function Page({ route }: { route: Route }) {
  switch (route.kind) {
    case "dashboard":
      return <Dashboard />;
    case "projects":
      return <Projects />;
    case "projectDetail":
      return <ProjectDetail projectId={route.projectId} />;
    case "book":
      return <BookAssembly projectId={route.projectId} />;
    case "itemDetail":
      return <ItemDetail projectId={route.projectId} itemId={route.itemId} />;
    case "newBook":
      return <NewBook />;
    case "review":
      return <ReviewQueue />;
    case "jobs":
      return <Jobs status={route.status} day={route.day} />;
    case "routes":
      return <RoutesPage />;
    case "templates":
      return <Templates />;
    case "tokens":
      return <Tokens />;
    case "exports":
      return <Exports />;
    case "login":
      return null;
  }
}

export function Shell({ route, onSignOut }: { route: Route; onSignOut: () => void }) {
  useDocumentTitle(shellTitleForRoute(route));
  async function handleSignOut() {
    try {
      await signOut();
    } finally {
      onSignOut();
      location.hash = "#/login";
    }
  }

  const idle =
    "flex items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100";
  const active =
    "flex items-center gap-2 rounded-md px-3 py-2 text-sm bg-(--accent) text-(--accent-contrast)";

  function navClassFor(navRoute: Route): string {
    if (navRoute.kind === route.kind) return active;
    if (navRoute.kind === "projects" && route.kind === "projectDetail") return active;
    if (navRoute.kind === "projects" && route.kind === "book") return active;
    if (navRoute.kind === "projects" && route.kind === "itemDetail") return active;
    return idle;
  }

  return (
    <div className="flex h-full flex-col bg-slate-50 dark:bg-slate-950 sm:flex-row">
      <aside className="hidden sm:flex sm:w-52 sm:shrink-0 sm:flex-col border-r border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <img src="/icon.svg" alt="" className="h-8 w-8 shrink-0" />
            <div className="font-semibold text-gray-900 dark:text-gray-100">Content Gen</div>
          </div>
        </div>
        <nav className="flex flex-1 flex-col gap-0.5 p-2" aria-label="Main">
          {NAV.map((item) => (
            <a key={item.label} href={hashForRoute(item.route)} className={navClassFor(item.route)}>
              {item.label}
            </a>
          ))}
        </nav>
        <div className="border-t border-gray-200 p-2 dark:border-gray-800">
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="w-full rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4 py-2 dark:border-gray-800 dark:bg-gray-900 sm:hidden">
          <img src="/icon.svg" alt="" className="h-8 w-8 shrink-0" />
          <span className="flex-1 truncate font-semibold text-gray-900 dark:text-gray-100">Content Gen</span>
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400"
          >
            Sign out
          </button>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto pb-16 sm:pb-0">
          <Page route={route} />
        </main>
      </div>

      <nav
        className="fixed inset-x-0 bottom-0 z-40 flex overflow-x-auto border-t border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 sm:hidden"
        aria-label="Main"
      >
        {NAV.map((item) => (
          <a
            key={item.label}
            href={hashForRoute(item.route)}
            className={`min-h-14 flex-1 justify-center ${navClassFor(item.route)}`}
          >
            {item.label}
          </a>
        ))}
      </nav>
    </div>
  );
}
